import { useState, useEffect, useRef } from "react";
import css from "./lesson.module.css";
import LessonInfo from "./lessonInfo";
import WrapVideo from "./wrapVideo";
import WordsComponent from "./wordsComponent";
import Discuss from "./discuss";
import QuestionCardExample from "./newTestings/QuestionCardExample";
import Dialogues from "./PracticeDialogues/Dialogues";
import CompletionScreen from "./CompletionScreen";
import FailedLessonScreen from "./FailedLessonScreen";
import { toggleVideoFavorite, getFavoriteStatus } from '../../lib/favoriteApi';
import { getLanguageLevelById } from "../../lib/getLanguageLevels";
import { getVideoCategories } from "../../lib/videoCategoryApi";
import { useTranslation } from "react-i18next";
import like from '../../img/like.png';
import likeOff from '../../img/likeOff.png';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReportModal from "./ReportModal";
import reportCss from "./reportModal.module.css";
import { markSectionCompleted, getLessonProgressWithSections } from "../../lib/videoLessonApi";
import { getTrialProgress, saveTrialProgress, saveLastAccessedLesson } from "../../lib/trialLessonProgress";
import { parseLessonProgress, getFirstIncompleteSection, SECTION_STEP_MAP, LESSON_SECTIONS } from "../../lib/lessonProgress";

const LessonBlock = ({ lesson, authUser }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isTrialLesson = location.pathname === '/trial-lesson';
  
  // Load trial progress if returning from auth
  const trialLessonProgress = isTrialLesson ? getTrialProgress('lessonState') : null;
  
  // Log progress restoration
  useEffect(() => {
    if (isTrialLesson && trialLessonProgress) {
      console.log('📦 Restoring trial lesson progress:', trialLessonProgress);
    }
  }, []);

  // Save last accessed lesson info when lesson loads
  useEffect(() => {
    if (lesson?.id) {
      const lessonData = {
        id: lesson.id,
        name: lesson.video?.name || lesson.name || 'Урок',
        video_lesson: lesson.video_lesson || lesson.id,
        completion_percentage: lesson.completion_percentage || 0,
        video: lesson.video
      };
      saveLastAccessedLesson(lesson.id, lessonData);
    }
  }, [lesson?.id]);

  const [videoP, setVideoP] = useState(trialLessonProgress?.videoP ?? true);
  const [learnWords, setLearnWords] = useState(trialLessonProgress?.learnWords ?? false);
  const [videoQ, setVideoQ] = useState(trialLessonProgress?.videoQ ?? false);
  const [dialogues, setDialogues] = useState(trialLessonProgress?.dialogues ?? false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [selectedButton, setSelectedButton] = useState(trialLessonProgress?.selectedButton ?? "speak");
  const [isLiked, setIsLiked] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [languageLevel, setLanguageLevel] = useState("");
  const [levels, setLevels] = useState([]);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState(authUser?.videoLessonsAll || []);
  const [currentStep, setCurrentStep] = useState(trialLessonProgress?.currentStep ?? 1); // Track current step (1-4)
  const [quizScore, setQuizScore] = useState(0);
  const [speakingScore, setSpeakingScore] = useState(0);
  const [lessonProgress, setLessonProgress] = useState(() => parseLessonProgress(null, lesson));
  const [showFailedLesson, setShowFailedLesson] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const vocabSectionCompletedRef = useRef(false);
  const progressReqRef = useRef(0);          // id of the latest progress request (stale responses are ignored)
  const wasCompletedRef = useRef(false);     // was the lesson already COMPLETED at the previous check
  const isMountedRef = useRef(true);
  const [isVerifyingResult, setIsVerifyingResult] = useState(false);
  const [savedQuizState, setSavedQuizState] = useState(null);
  const [levelStyles, setLevelStyles] = useState({
    bgColor: "#e0e0e0",
    textColor: "#000",
    fullName: "Unknown Level",
    order: "-",
  });

  const totalSteps = 4;

  // Guests have no server-side progress: mark as done every step before the current one (old logic)
  const isGuest = !authUser?.isAuthenticated;
  const sidebarSections = isGuest
    ? LESSON_SECTIONS.reduce((acc, key) => {
        acc[key] = { status: SECTION_STEP_MAP[key] < currentStep ? "completed" : "not_started" };
        return acc;
      }, {})
    : lessonProgress.sections;
  const sidebarCompletedCount = isGuest ? Math.max(0, currentStep - 1) : lessonProgress.completedCount;
  
  // Save lesson state to localStorage for trial lesson
  useEffect(() => {
    if (isTrialLesson) {
      saveTrialProgress('lessonState', {
        videoP,
        learnWords,
        videoQ,
        dialogues,
        selectedButton,
        currentStep
      });
    }
  }, [isTrialLesson, videoP, learnWords, videoQ, dialogues, selectedButton, currentStep]);
  
  // Step data for guidance
  const stepData = [
    {
      guidanceLabel: 'What to do now',
      guidanceTitle: 'Watch Video',
      guidanceSub: 'Watch a short real-life dialogue, then move on to the words.',
      ctaText: 'Next: Words',
      iconSvg: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
          <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
        </svg>
      )
    },
    {
      guidanceLabel: 'What to do now',
      guidanceTitle: 'Learn Words',
      guidanceSub: 'Learn new words from the video, then click "Next".',
      ctaText: 'Next: Quiz',
      iconSvg: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
        </svg>
      )
    },
    {
      guidanceLabel: 'What to do now',
      guidanceTitle: 'Check your understanding',
      guidanceSub: 'Check your understanding, then click "Next."',
      ctaText: 'Next: Speaking',
      iconSvg: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
        </svg>
      )
    },
    {
      guidanceLabel: 'What to do now',
      guidanceTitle: 'Practice the dialogue',
      guidanceSub: 'Speak to AI in English. This is the last step of the lesson!',
      ctaText: '',
      iconSvg: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
        </svg>
      )
    }
  ];

  const currentStepData = stepData[currentStep - 1] || stepData[0];

  const levelMapping = {
    "pre-A1": {
      fullName: "Pre-Beginner",
      order: 0,
      bgColor: "#c8e6c9",
      textColor: "#1b5e20",
      name: "pre-A1",
    },
    A1: {
      fullName: "Beginner",
      order: 1,
      bgColor: "#e8f5e9",
      textColor: "#2e7d32",
      name: "A1",
    },
    A2: {
      fullName: "Elementary",
      order: 2,
      bgColor: "#fff3e0",
      textColor: "#f57c00",
      name: "A2",
    },
    B1: {
      fullName: "Intermediate",
      order: 3,
      bgColor: "#e3f2fd",
      textColor: "#1565c0",
      name: "B1",
    },
    B2: {
      fullName: "Upper-Intermediate",
      order: 4,
      bgColor: "#ede7f6",
      textColor: "#673ab7",
      name: "B2",
    },
    C1: {
      fullName: "Advanced",
      order: 5,
      bgColor: "#ffebee",
      textColor: "#d32f2f",
      name: "C1",
    },
    C2: {
      fullName: "Proficiency",
      order: 6,
      bgColor: "#fce4ec",
      textColor: "#c2185b",
      name: "C2",
    },
  };

  //console.log(authUser)

  // Check lesson completion status dynamically
  useEffect(() => {
    if (authUser?.videoLessonsAll) {
      setVideos(authUser.videoLessonsAll);
    }
  }, [authUser?.videoLessonsAll]);

  const hideLessonViews = () => {
    setVideoP(false);
    setLearnWords(false);
    setVideoQ(false);
    setDialogues(false);
  };

  const goToSection = (section) => {
    const step = SECTION_STEP_MAP[section] || 1;
    setIsVerifyingResult(false);
    setShowFailedLesson(false);
    setShowCompletion(false);
    setCurrentStep(step);

    setVideoP(section === "video");
    setLearnWords(section === "vocabulary");
    setVideoQ(section === "quiz");
    setDialogues(section === "ai_dialog");
    setSelectedButton(
      section === "video" ? "speak" : section === "vocabulary" ? "learn" : section === "quiz" ? "discuss" : "none"
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // silent: only refresh section statuses, never switch the screen
  const applyProgressResult = (progressData, { initial = false, silent = false } = {}) => {
    const parsed = parseLessonProgress(progressData, lesson);
    setLessonProgress(parsed);

    const justCompleted = parsed.isCompleted && !wasCompletedRef.current;
    wasCompletedRef.current = parsed.isCompleted;

    if (parsed.isCompleted && !silent && (initial || justCompleted)) {
      setShowFailedLesson(false);
      setShowCompletion(true);
      hideLessonViews();
    }

    return parsed;
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // DEV ONLY (dev buttons in Dialogues): pretend the backend already completed ai_dialog; local UI test only
  const withDevAiDialogCompleted = (data) => {
    const sections = { ...(data?.section_progress || lesson?.section_progress || {}) };
    sections.ai_dialog = { ...(sections.ai_dialog || {}), status: "completed" };
    const isDone = (sec) => (sec?.status || sec || "").toString().toLowerCase() === "completed";
    const othersDone = LESSON_SECTIONS.filter((key) => key !== "ai_dialog").every((key) => isDone(sections[key]));
    return { ...(data || {}), section_progress: sections, ...(othersDone ? { completion_status: "COMPLETED" } : {}) };
  };

  const checkAndUpdateCompletion = async ({ fromFinalStep = false, silent = false, devForceComplete = false } = {}) => {
    if (!lesson?.id) return null;

    const reqId = ++progressReqRef.current;
    // after the dialogue the backend needs some time to evaluate it - poll instead of failing at once
    const maxAttempts = fromFinalStep ? 6 : 1;
    const delayMs = 1500;

    if (fromFinalStep) setIsVerifyingResult(true);

    let parsed = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        let progressData = await getLessonProgressWithSections(lesson.id);
        if (!isMountedRef.current || reqId !== progressReqRef.current) return null; // a newer request took over
        if (devForceComplete) progressData = withDevAiDialogCompleted(progressData);

        parsed = applyProgressResult(progressData, {
          silent: silent || (fromFinalStep && !parsed?.isCompleted),
        });
        // always allow the completion screen on the final step
        if (fromFinalStep && parsed.isCompleted) {
          setShowFailedLesson(false);
          setShowCompletion(true);
          hideLessonViews();
        }

        const dialogDone = parsed.sections.ai_dialog.status === "completed";
        if (!fromFinalStep || parsed.isCompleted || dialogDone) break;
      } catch (error) {
        console.error("Error checking completion status:", error);
      }
      if (attempt < maxAttempts) await sleep(delayMs);
      if (!isMountedRef.current || reqId !== progressReqRef.current) return null;
    }

    if (fromFinalStep) {
      setIsVerifyingResult(false);
      if (!parsed?.isCompleted) {
        setShowCompletion(false);
        setShowFailedLesson(true);
        hideLessonViews();
      }
    }

    return parsed;
  };

  useEffect(() => {
    if (!lesson?.id) return;

    let cancelled = false;
    wasCompletedRef.current = false;
    const loadProgress = async () => {
      const progressData = await getLessonProgressWithSections(lesson.id);
      if (cancelled) return;
      applyProgressResult(progressData || lesson, { initial: true });
    };

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [lesson?.id]);

  // Refresh section statuses whenever the user switches step (sidebar, "Next", etc.)
  const firstStepRenderRef = useRef(true);
  useEffect(() => {
    if (firstStepRenderRef.current) {
      firstStepRenderRef.current = false;
      return;
    }
    if (!lesson?.id || showCompletion || showFailedLesson || isVerifyingResult) return;
    checkAndUpdateCompletion({ silent: true });
  }, [currentStep]);

  // Check if vocabulary section was already completed from lesson data
  useEffect(() => {
    const vocabProgress = lesson?.section_progress?.vocabulary;
    if (vocabProgress && vocabProgress.status === 'completed') {
      vocabSectionCompletedRef.current = true;
    }
  }, [lesson]);

  // Mark vocabulary section as completed when user opens words
  useEffect(() => {
    if (learnWords && lesson?.id && !vocabSectionCompletedRef.current) {
      vocabSectionCompletedRef.current = true;
      markSectionCompleted(lesson.id, 'vocabulary', 'completed')
        .then(() => checkAndUpdateCompletion())
        .catch((err) => console.error('Failed to mark vocabulary section:', err));
    }
  }, [learnWords, lesson?.id]);

  // Fetch lesson data and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesData = await getVideoCategories();
        setCategories(categoriesData.results);

        // Fetch language level and set styles
        if (lesson?.video?.languageLevel) {
          const level = await getLanguageLevelById(lesson.video.languageLevel);
          setLanguageLevel(level.name);
          if (levelMapping[level.name]) {
            setLevelStyles(levelMapping[level.name]);
          }
        }

        // Fetch favorite status
        if (lesson?.id) {
          const { isFavorited, favoriteId: favoriteIdValue } = await getFavoriteStatus(lesson.id);
          setIsLiked(isFavorited);
          setFavoriteId(favoriteIdValue);
        }
      } catch (error) {
        console.error("Error fetching lesson data:", error);
        // Fallback to lesson data if API call fails
        if (lesson) {
          const isFavorited = lesson.is_favorited || false;
          const favoriteIdValue = lesson.favorite_id || null;
          setIsLiked(isFavorited);
          setFavoriteId(favoriteIdValue);
        }
      }
    };

    if (lesson) {
      fetchData();
    }
  }, [lesson]);

  const handleSelect = (button) => {
    setSelectedButton(button);
  };

  // Expose handlers globally for Dialogues component to switch views
  useEffect(() => {
    window.lessonInfoHandlers = {
      handleSelect: (button) => {
        let targetIndex = -1;
        let newStep = currentStep;
        
        if (button === 'words') {
          setShowCompletion(false);
          setShowFailedLesson(false);
          setVideoP(false);
          setLearnWords(true);
          setDialogues(false);
          setVideoQ(false);
          setSelectedButton("learn");
          targetIndex = 1; // Second button (Study Words)
          newStep = 2;
        } else if (button === 'speak') {
          setShowCompletion(false);
          setShowFailedLesson(false);
          setVideoP(true);
          setLearnWords(false);
          setVideoQ(false);
          setDialogues(false);
          setSelectedButton("speak");
          targetIndex = 0; // First button (Watch Video)
          newStep = 1;
        } else if (button === 'test') {
          setShowCompletion(false);
          setShowFailedLesson(false);
          setVideoP(false);
          setLearnWords(false);
          setVideoQ(true);
          setDialogues(false);
          setSelectedButton("discuss");
          targetIndex = 2; // Third button (Check Understanding)
          newStep = 3;
        } else if (button === 'dialogue') {
          setShowCompletion(false);
          setShowFailedLesson(false);
          setVideoP(false);
          setLearnWords(false);
          setVideoQ(false);
          setDialogues(true);
          setSelectedButton("none");
          targetIndex = 3; // Fourth button (Practice Dialogues)
          newStep = 4;
        }
        
        setCurrentStep(newStep);
        
        // Update active state for buttons using data attribute
        if (targetIndex >= 0) {
          setTimeout(() => {
            const choiceWraps = document.querySelectorAll('[data-choice-index]');
            choiceWraps.forEach((el) => {
              const index = parseInt(el.getAttribute('data-choice-index'));
              if (index === targetIndex) {
                el.classList.add('active');
              } else {
                el.classList.remove('active');
              }
            });
          }, 100);
        }
      }
    };

    return () => {
      delete window.lessonInfoHandlers;
    };
  }, [currentStep]);

  // Function to advance to next step
  const advanceStep = () => {
    if (currentStep < 4) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Update view based on next step
      if (nextStep === 1) {
        setVideoP(true);
        setLearnWords(false);
        setVideoQ(false);
        setDialogues(false);
        setShowCompletion(false);
        setSelectedButton("speak");
      } else if (nextStep === 2) {
        setVideoP(false);
        setLearnWords(true);
        setVideoQ(false);
        setDialogues(false);
        setShowCompletion(false);
        setSelectedButton("learn");
      } else if (nextStep === 3) {
        setVideoP(false);
        setLearnWords(false);
        setVideoQ(true);
        setDialogues(false);
        setShowCompletion(false);
        setSelectedButton("discuss");
      } else if (nextStep === 4) {
        setVideoP(false);
        setLearnWords(false);
        setVideoQ(false);
        setDialogues(true);
        setShowCompletion(false);
        setSelectedButton("none");
      }
      
      // Update active state for buttons
      setTimeout(() => {
        const choiceWraps = document.querySelectorAll('[data-choice-index]');
        choiceWraps.forEach((el, i) => {
          if (i === nextStep - 1) {
            el.classList.add('active');
          } else {
            el.classList.remove('active');
          }
        });
      }, 100);
    }
  };

  // Expose advanceStep globally for child components
  useEffect(() => {
    window.advanceStep = advanceStep;
    
    return () => {
      delete window.advanceStep;
    };
  }, [currentStep, videoP, learnWords, videoQ, dialogues]);

  const handleRegister = () => {
    // Navigate to registration page
    window.location.href = "/signup";
  };

  const findParentCategory = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return null;

    if (category.parent === null) {
      return category;
    }

    return categories.find(cat => cat.id === category.parent);
  };

  const getCategoryIcon = (categoryId) => {
    const parentCategory = findParentCategory(categoryId);
    if (!parentCategory) return '🌐';

    const name = parentCategory.name?.toLowerCase() || '';

    // Check for more specific categories first (order matters!)
    if (name.includes('talk') || name.includes('dialogue') || name.includes('діалог') || name.includes('розмов') || name.includes('спілкування') || name.includes('rozmawiać') || name.includes('dialog') || name.includes('socializ')) return '🗣️';
    if (name.includes('all') || name.includes('всі') || name.includes('wszystkie')) return '🌐';
    if (name.includes('travel') || name.includes('подорож') || name.includes('podróż')) return '✈️';
    if (name.includes('work') || name.includes('робот') || name.includes('praca') || name.includes('business') || name.includes('бізнес')) return '💼';
    if (name.includes('education') || name.includes('освіт') || name.includes('edukacja') || name.includes('learn')) return '📚';
    if (name.includes('health') || name.includes('здоров') || name.includes('zdrowie')) return '❤️';
    if (name.includes('news') || name.includes('новин') || name.includes('wiadomości') || name.includes('tech') || name.includes('технолог')) return '📱';
    if (name.includes('leisure') || name.includes('дозвілля') || name.includes('rozrywka') || name.includes('entertainment') || name.includes('розваг')) return '🎮';
    if (name.includes('daily') || name.includes('щоден') || name.includes('codzienn')) return '📅';

    return '🌐';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  const handleFavoriteToggle = async () => {
    if (isTogglingFavorite || !lesson?.id) return;
    setIsTogglingFavorite(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousFavoriteId = favoriteId;

    // Optimistic update
    setIsLiked(!isLiked);

    try {
      const response = await toggleVideoFavorite(lesson.id, favoriteId, isLiked);

      if (!isLiked) {
        // Adding to favorites - response should contain the new favorite ID
        setFavoriteId(response.id);
        console.log('Added to favorites with ID:', response.id);
      } else {
        // Removing from favorites
        setFavoriteId(null);
        console.log('Removed from favorites');
      }

      // Fetch updated favorite status to ensure consistency
      const { isFavorited, favoriteId: newFavoriteId } = await getFavoriteStatus(lesson.id);
      setIsLiked(isFavorited);
      setFavoriteId(newFavoriteId);
      console.log('Favorite status updated successfully');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert optimistic update on error
      setIsLiked(previousIsLiked);
      setFavoriteId(previousFavoriteId);
      // Show user-friendly error message
      alert(t('Failed to update favorite status. Please try again.'));
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <div className={css.wrapAllN}>
      
      {currentStep !== 4 && !showCompletion && !showFailedLesson && (
        <>
          {/* Design 27 — Compact Top Bar */}
          <div className={css.d27topbar}>
            <div className={css.d27left}>
              <div className={css.d27step}>{t("Step")} {currentStep} {t("of")} 4</div>
              <div className={css.d27info}>
                <h2 className={css.d27title}>{t(currentStepData.guidanceTitle)}</h2>
                <p className={css.d27desc}>{t(currentStepData.guidanceSub)}</p>
              </div>
            </div>
            <div className={css.d27right}>
              {(!authUser || !authUser.isAuthenticated) && isTrialLesson && (
                <button className={css.d27regBtn} onClick={() => {
                  // Save current state before navigating
                  const stateToSave = {
                    videoP,
                    learnWords,
                    videoQ,
                    dialogues,
                    selectedButton,
                    currentStep
                  };
                  console.log('💾 Saving lesson state before navigation:', stateToSave);
                  saveTrialProgress('lessonState', stateToSave);
                  console.log('✅ State saved, navigating to signup...');
                  navigate('/signup', { state: { fromTrialLesson: true } });
                }}>
                  {t("Save progress")}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
              )}
              {/* currentStep < 4 && (
                <button className={css.d27btnNext} onClick={() => window.advanceStep && window.advanceStep()}>
                  {t(currentStepData.ctaText || 'Далі')}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
              )*/}
            </div>
          </div>
        </>
      )}

      {/* Target for Dialogues Header (Design 29) */}
      <div id="dialogue-header-portal-target"></div>

      {/* Main Container */}
      <div className={css.mainContainer}>
        {/* Lesson Content */}
        <div className={css.lessonContent}>
          {/* Video/Content Area */}
          <div className={css.leftWrap}>
            {showFailedLesson && (
                <FailedLessonScreen 
                  lesson={lesson}
                  progress={lessonProgress}
                  authUser={authUser}
                  onGoToSection={goToSection}
                  onRepeat={() => goToSection(getFirstIncompleteSection(lessonProgress))}
                />
            )}

            {showCompletion && !showFailedLesson && (
              <CompletionScreen 
                lesson={lesson}
                progress={lessonProgress}
                quizScore={quizScore}
                speakingScore={speakingScore}
                authUser={authUser}
              />
            )}
            {!showCompletion && !showFailedLesson && videoP && <WrapVideo lesson={lesson} sectionProgress={lessonProgress.sections} authUser={authUser} videos={videos} setVideos={setVideos} onActivityComplete={() => checkAndUpdateCompletion()} />}
            {!showCompletion && !showFailedLesson && learnWords && (
              <div className={css.wrWhereW}>
                <WordsComponent lesson={lesson} onActivityComplete={() => checkAndUpdateCompletion()} />
              </div>
            )}
            {!showCompletion && !showFailedLesson && videoQ && (
              <div style={{width:'100%', flex:1, overflow:'auto'}}>
                <QuestionCardExample
                  questions={lesson.video.questions}
                  onActivityComplete={(score, meta) => {
                    if (score !== undefined) setQuizScore(score);
                    checkAndUpdateCompletion({ silent: meta?.silent });
                  }}
                  lessonId={lesson?.id}
                  videoLessonId={lesson?.id}
                  savedState={savedQuizState}              
                  onStateChange={setSavedQuizState}
                />
              </div>
            )}
            {isVerifyingResult && !showCompletion && !showFailedLesson && (
              <div style={{ width: '100%', padding: '80px 20px', textAlign: 'center', fontWeight: 600 }}>
                {t('Checking your result...')}
              </div>
            )}
            {!showCompletion && !showFailedLesson && !isVerifyingResult && dialogues && (
              <div style={{width:'100%', flex:1, overflow:'auto'}}>
                <Dialogues 
                  lesson={lesson} 
                  authUser={authUser}
                  onActivityComplete={(score, meta) => {
                    if (score !== undefined) setSpeakingScore(score);
                    checkAndUpdateCompletion({
                      fromFinalStep: meta?.fromFinalStep !== false,
                      devForceComplete: !!meta?.devForceComplete,
                    });
                  }}
                />
              </div>
            )}
            
            <div className={css.underTaskRow}>
            {/* Report Problem Link */}
            {!showCompletion && !showFailedLesson && (
              <div className={reportCss.rptLink} onClick={() => setShowReportModal(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {t('reportProblemText', 'Проблема з уроком? Повідомити')}
              </div>
            )}

          {videoP == false && dialogues == false && !showCompletion && !showFailedLesson && (
            <button className={css.d27btnNext} onClick={() => window.advanceStep && window.advanceStep()}>
                  {t(currentStepData.ctaText || 'Далі')}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>)}
                </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={css.rightWrap}>
          <LessonInfo
            handleSelect={handleSelect}
            lesson={lesson}
            setLearnWords={setLearnWords}
            setVideoP={setVideoP}
            setSelectedButton={setSelectedButton}
            selectedButton={selectedButton}
            setVideoQ={setVideoQ}
            videoQ={videoQ}
            dialogues={dialogues}
            setDialogues={setDialogues}
            isCompletionShown={showCompletion}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            sectionProgress={sidebarSections}
            completedCount={sidebarCompletedCount}
            authUser={authUser}
            isTrialLesson={isTrialLesson}
          />
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal 
        isOpen={showReportModal} 
        onClose={() => setShowReportModal(false)} 
        lesson={lesson} 
      />
    </div>
  );
};
export default LessonBlock;
